import { createAsyncThunk } from '@reduxjs/toolkit';
import api from 'api';
import IRecycleBinItem, {  IRecycleBinDocument, IRecycleBinFolder } from 'interfaces/IRecycleBinItem';
import { RootState } from 'store';

const parseApiDate = (dateStr: string): string => {
  if (!dateStr) return dateStr;
  const match = dateStr.match(/^(\d{2})\.(\d{2})\.(\d{4}),\s*(\d{2}:\d{2}:\d{2})$/);
  if (!match) return dateStr;
  const [, day, month, year, time] = match;
  return `${year}-${month}-${day}T${time}`;
};

export const fetchRecycleBinItems = createAsyncThunk(
  'recycleBin/fetchAll',
  async (props: { roomId: string }, thunkAPI) => {
    try {
      const [docsRes, foldersRes] = await Promise.all([
        api.getRecycleBinDocuments(props.roomId),
        api.getRecycleBinFolders(props.roomId),
      ]);

      const documents = docsRes.data as IRecycleBinDocument[];
      const folders = foldersRes.data as IRecycleBinFolder[];

      const docItems: IRecycleBinItem[] = documents.map((doc) => ({
        id: doc.id,
        name: doc.name,
        type: 'file',
        extension: doc.extension,
        deleted_at: parseApiDate(doc.deleted_at),
        deletion_author: doc.deletion_author,
        parent_folder_id: doc.folder_id ?? null,
        is_confidential: doc.is_confidential,
      }));

      const folderItems: IRecycleBinItem[] = folders.map((folder) => ({
        id: folder.id,
        name: folder.name,
        type: 'folder',
        deleted_at: parseApiDate(folder.deleted_at),
        deletion_author: folder.deletion_author,
        parent_folder_id: folder.parent_folder_id,
        is_confidential: folder.is_confidential,
      }));

      return [...folderItems, ...docItems];
    } catch (e) {
      return thunkAPI.rejectWithValue('Failed to fetch recycle bin items');
    }
  }
);

export const forceDeleteRecycleBinItems = createAsyncThunk(
  'recycleBin/forceDelete',
  async (
    props: { roomId: string; selectedIds: string[] },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as RootState;
    const { items } = state.recycleBin;
    try {
      const selectedItems = items.filter((item) =>
        props.selectedIds.includes(item.id)
      );

      const documentsIds = selectedItems
        .filter((item) => item.type === 'file')
        .map((item) => item.id);

      const foldersIds = selectedItems
        .filter((item) => item.type === 'folder')
        .map((item) => item.id);

      await Promise.all([
        documentsIds.length > 0
          ? api.deleteRecycleBinDocuments({ room_id: props.roomId, documents_ids: documentsIds })
          : Promise.resolve(),
        foldersIds.length > 0
          ? api.deleteRecycleBinFolders({
              room_id: props.roomId,
              folders_ids: foldersIds,
              // is_bulk: foldersIds.length > 1,
              is_bulk: true, // при false происходит удаление только папки как оббертки, вложенные файлы оказываются перенесенными в корзину
            })
          : Promise.resolve(),
      ]);

      thunkAPI.dispatch(fetchRecycleBinItems({ roomId: props.roomId }));

      return { deletedDocuments: documentsIds, deletedFolders: foldersIds };
    } catch (e) {
      return thunkAPI.rejectWithValue('Failed to delete items');
    }
  }
);

export const restoreRecycleBinItems = createAsyncThunk(
  'recycleBin/restore',
  async (
    props: { roomId: string; selectedIds: string[] },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as RootState;
    const { items } = state.recycleBin;
    try {
      const selectedItems = items.filter((item) =>
        props.selectedIds.includes(item.id)
      );

      const documentsIds = selectedItems
        .filter((item) => item.type === 'file')
        .map((item) => item.id);

      const foldersIds = selectedItems
        .filter((item) => item.type === 'folder')
        .map((item) => item.id);

      await Promise.all([
        documentsIds.length > 0
          ? api.restoreRecycleBinDocuments({ room_id: props.roomId, documents_ids: documentsIds })
          : Promise.resolve(),
        foldersIds.length > 0
          ? api.restoreRecycleBinFolders({ room_id: props.roomId, folders_ids: foldersIds })
          : Promise.resolve(),
      ]);

      thunkAPI.dispatch(fetchRecycleBinItems({ roomId: props.roomId }));

      return { restoredDocuments: documentsIds, restoredFolders: foldersIds };
    } catch (e) {
      return thunkAPI.rejectWithValue('Failed to restore items');
    }
  }
);

export const restoreRecycleBinItemsInRoot = createAsyncThunk(
  'recycleBin/restoreInRoot',
  async (
    props: { roomId: string; selectedIds: string[] },
    thunkAPI
  ) => {
    const state = thunkAPI.getState() as RootState;
    const { items } = state.recycleBin;
    try {
      const selectedItems = items.filter((item) =>
        props.selectedIds.includes(item.id)
      );

      const documentsIds = selectedItems
        .filter((item) => item.type === 'file')
        .map((item) => item.id);

      const foldersIds = selectedItems
        .filter((item) => item.type === 'folder')
        .map((item) => item.id);

      await Promise.all([
        documentsIds.length > 0
          ? api.restoreRecycleBinDocumentsInRoot({ room_id: props.roomId, documents_ids: documentsIds })
          : Promise.resolve(),
        foldersIds.length > 0
          ? api.restoreRecycleBinFoldersInRoot({ room_id: props.roomId, folders_ids: foldersIds })
          : Promise.resolve(),
      ]);

      thunkAPI.dispatch(fetchRecycleBinItems({ roomId: props.roomId }));

      return { restoredDocuments: documentsIds, restoredFolders: foldersIds };
    } catch (e) {
      return thunkAPI.rejectWithValue('Failed to restore items in root');
    }
  }
);
